import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shield, Mail, Lock, Eye, EyeOff, Check, X, Crown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiClient } from "@/api/ApiClient";

interface CredentialsSettingsProps {
  user?: {
    id?: string;
    email?: string;
    name?: string;
    isPremium?: boolean;
    emailVerified?: boolean;
  };
  onCredentialsUpdated?: (newEmail?: string) => void;
}

export const CredentialsSettings = ({ user, onCredentialsUpdated }: CredentialsSettingsProps) => {
  const [showSetCredentialsModal, setShowSetCredentialsModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
    emailPassword: false
  });
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    emailPassword: "",
    newEmail: ""
  });

  // Password match validation
  const isPasswordMatch = formData.newPassword === formData.confirmPassword;
  const showPasswordMismatch = formData.confirmPassword && !isPasswordMatch;
  
  const { toast } = useToast();

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSetCredentials = async () => {
    if (!formData.email || !formData.password) {
      toast({
        title: "Error",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.setCredentials(formData.email, formData.password);
      
      if (response.success) {
        toast({
          title: "Success",
          description: "Email and password have been set successfully.",
        });
        setShowSetCredentialsModal(false);
        setFormData(prev => ({ ...prev, email: "", password: "" }));
        onCredentialsUpdated?.(formData.email);
      } else {
        throw new Error(response.message || "Failed to set credentials");
      }
    } catch (error: any) {
      console.error('Error setting credentials:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to set credentials. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "New passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    if (formData.newPassword.length < 6) {
      toast({
        title: "Error",
        description: "New password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.updatePassword(formData.currentPassword, formData.newPassword);
      
      if (response.success) {
        toast({
          title: "Success",
          description: "Password has been changed successfully.",
        });
        setShowChangePasswordModal(false);
        setFormData(prev => ({ 
          ...prev, 
          currentPassword: "", 
          newPassword: "", 
          confirmPassword: "" 
        }));
      } else {
        throw new Error(response.message || "Failed to change password");
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to change password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!formData.emailPassword || !formData.newEmail) {
      toast({
        title: "Error",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.newEmail)) {
      toast({
        title: "Error",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.updateEmail(formData.emailPassword, formData.newEmail);
      
      if (response.success) {
        toast({
          title: "Success",
          description: "Email has been changed successfully. Please verify your new email.",
        });
        setShowChangeEmailModal(false);
        setFormData(prev => ({ 
          ...prev, 
          emailPassword: "", 
          newEmail: "" 
        }));
        onCredentialsUpdated?.(formData.newEmail);
      } else {
        throw new Error(response.message || "Failed to change email");
      }
    } catch (error: any) {
      console.error('Error changing email:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to change email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Current Email Display - Only show if user has email */}
      {user?.email && (
        <Card className="p-4 border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-full">
                <Mail className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h3 className="font-semibold">Current Email</h3>
                <p className="text-sm text-muted-foreground">
                  {user.email}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {user.emailVerified ? (
                    <>
                      <Check className="h-3 w-3 text-green-500" />
                      <span className="text-xs text-green-600 font-medium">Verified</span>
                    </>
                  ) : (
                    <>
                      <X className="h-3 w-3 text-red-500" />
                      <span className="text-xs text-red-600 font-medium">Not verified</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Set Credentials - Only show if user doesn't have email */}
      {!user?.email && (
        <Card className="p-4 border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-full">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold">Set Credentials</h3>
                <p className="text-sm text-muted-foreground">
                  Add email and password for account security
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowSetCredentialsModal(true)}
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
            >
              Set
            </Button>
          </div>
        </Card>
      )}

      {/* Change Password - Only show if user has email */}
      {user?.email && (
        <Card className="p-4 border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-full">
                <Lock className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">Change Password</h3>
                <p className="text-sm text-muted-foreground">
                  Update your password for better security
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowChangePasswordModal(true)}
              className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              Change
            </Button>
          </div>
        </Card>
      )}

      {/* Change Email - Only show if user has email */}
      {user?.email && (
        <Card className="p-4 border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-full">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold">Change Email</h3>
                <p className="text-sm text-muted-foreground">
                  Update your email address
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowChangeEmailModal(true)}
              className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
            >
              Change
            </Button>
          </div>
        </Card>
      )}

      {/* Set Credentials Modal */}
      <Dialog open={showSetCredentialsModal} onOpenChange={setShowSetCredentialsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Set Credentials
            </DialogTitle>
            <DialogDescription>
              Add email and password to secure your account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="set-email">Email</Label>
              <Input
                id="set-email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="set-password">Password</Label>
              <div className="relative">
                <Input
                  id="set-password"
                  type={showPasswords.newPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility('newPassword')}
                >
                  {showPasswords.newPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowSetCredentialsModal(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleSetCredentials}
                disabled={isLoading}
              >
                {isLoading ? "Setting..." : "Set"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showChangePasswordModal} onOpenChange={setShowChangePasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-green-600" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Enter your current password and new password
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showPasswords.currentPassword ? "text" : "password"}
                  placeholder="Enter current password"
                  value={formData.currentPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility('currentPassword')}
                >
                  {showPasswords.currentPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPasswords.newPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={formData.newPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility('newPassword')}
                >
                  {showPasswords.newPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showPasswords.confirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className={showPasswordMismatch ? "border-red-500 focus:border-red-500" : ""}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility('confirmPassword')}
                >
                  {showPasswords.confirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {/* Password match indicator */}
              {formData.confirmPassword && (
                <div className="flex items-center gap-2 text-xs">
                  {isPasswordMatch ? (
                    <>
                      <Check className="h-3 w-3 text-green-500" />
                      <span className="text-green-600">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <X className="h-3 w-3 text-red-500" />
                      <span className="text-red-600">Passwords do not match</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowChangePasswordModal(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleChangePassword}
                disabled={isLoading || showPasswordMismatch}
              >
                {isLoading ? "Changing..." : "Change Password"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Email Modal */}
      <Dialog open={showChangeEmailModal} onOpenChange={setShowChangeEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-600" />
              Change Email
            </DialogTitle>
            <DialogDescription>
              Enter your current password and new email address
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email-password">Current Password</Label>
              <div className="relative">
                <Input
                  id="email-password"
                  type={showPasswords.emailPassword ? "text" : "password"}
                  placeholder="Enter current password"
                  value={formData.emailPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, emailPassword: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility('emailPassword')}
                >
                  {showPasswords.emailPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-email">New Email</Label>
              <Input
                id="new-email"
                type="email"
                placeholder="new@email.com"
                value={formData.newEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, newEmail: e.target.value }))}
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowChangeEmailModal(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleChangeEmail}
                disabled={isLoading}
              >
                {isLoading ? "Changing..." : "Change Email"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
